import { FormControl, FormGroup } from "@angular/forms";
import { AssessmentPdfGenerator } from "src/app/patients/dashboard-page/assessments-history-tab/assessment-to-pdf";
import { ControlsOf, AssessmentHelpers } from "./shared";
import { Patient } from "../patient.model";

interface GeneralAppearanceFields {
  /**
   * The patient's level of consciousness.
   *
   * Options: lethargic or somnolent.
   */
  loc: string;

  /**
   * The patient's sense of orientation.
   *
   * Options: not to person, not to place, not to time, and not to situation.
   * 
   * for pediatric, 3 additional options: Cries, Grunts, Unable to assess.
   */
  orientation: string[];

  /**
   * Whether the patient is letharic (true) or not (false).
   */
  lethargic: boolean;
  inappropriateForAge: boolean;
  distress: boolean;

  // Ob only.
  babyBonding: boolean;
  hasBreastAbnormalities: boolean;
  nippleAbnormalitiesNotes: string;
  breastAbnormalitiesNotes: string;

  /**
   * Whether a support person is at bedside.
   */
  supportPersonAtBedside: boolean;

  /**
   * Type of support person at bedside.
   * Options: Parent/Guardian, Sibling, Other
   */
  supportPersonType: string;

  /**
   * Name of support person at bedside.
   */
  supportPersonName: string;

  otherFindings: string;
}

export type GeneralAppearanceFormGroup = FormGroup<ControlsOf<GeneralAppearanceFields>>;

export const GeneralAppearanceAssessmentHelpers: AssessmentHelpers<GeneralAppearanceAssessment, Patient> = {
  createFormGroup: createGeneralAppearanceFormGroup,
  toPDF: generalAppearanceAssessmentToPDF,
  formToObject: formToGeneralAppearanceAssessment,
  initialState: {
    loc: '', // lethargic, somnolent
    orientation: [],
    lethargic: false,
    inappropriateForAge: false,
    distress: false,
    babyBonding: false,
    hasBreastAbnormalities: false,
    nippleAbnormalitiesNotes: '',
    breastAbnormalitiesNotes: '',
    supportPersonAtBedside: false,
    supportPersonType: '',
    supportPersonName: '',
    otherFindings: '',
  },
};

/**
 * Holds information about the patient's general appearance assessment.
 */
export class GeneralAppearanceAssessment {
  loc: string;
  orientation: string[];
  lethargic: boolean;
  inappropriateForAge: boolean;
  distress: boolean;
  babyBonding: boolean;
  hasBreastAbnormalities: boolean;
  nippleAbnormalitiesNotes: string;
  breastAbnormalitiesNotes: string;
  supportPersonAtBedside: boolean;
  supportPersonType: string;
  supportPersonName: string;
  otherFindings: string;

  constructor(data: Partial<GeneralAppearanceFields>) {
    const is = GeneralAppearanceAssessmentHelpers.initialState;
    this.loc = data.loc || is['loc'];
    this.orientation = data.orientation || is['orientation'];
    this.lethargic = data.lethargic ?? is['lethargic'];
    this.inappropriateForAge = data.inappropriateForAge ?? is['inappropriateForAge'];
    this.distress = data.distress ?? is['distress'];
    this.babyBonding = data.babyBonding ?? is['babyBonding'];
    this.hasBreastAbnormalities = data.hasBreastAbnormalities ?? is['hasBreastAbnormalities'];
    this.nippleAbnormalitiesNotes = data.nippleAbnormalitiesNotes || is['nippleAbnormalitiesNotes'];
    this.breastAbnormalitiesNotes = data.breastAbnormalitiesNotes || is['breastAbnormalitiesNotes'];
    this.supportPersonAtBedside = data.supportPersonAtBedside || is['supportPersonAtBedside'];
    this.supportPersonType = data.supportPersonType ?? is['supportPersonType'];
    this.supportPersonName = data.supportPersonName ?? is['supportPersonName'];
    this.otherFindings = data.otherFindings || is['otherFindings'];
  }
}


function createGeneralAppearanceFormGroup(generalAppearanceAssessment?: GeneralAppearanceAssessment) {
  const data = generalAppearanceAssessment || new GeneralAppearanceAssessment(GeneralAppearanceAssessmentHelpers.initialState);
  return new FormGroup<ControlsOf<GeneralAppearanceFields>>({
    loc: new FormControl<string | null>(data.loc),
    orientation: new FormControl<string[] | null>(data.orientation),
    lethargic: new FormControl<boolean | null>(data.lethargic),
    inappropriateForAge: new FormControl<boolean | null>(data.inappropriateForAge),
    distress: new FormControl<boolean | null>(data.distress),
    babyBonding: new FormControl<boolean | null>(data.babyBonding),
    hasBreastAbnormalities: new FormControl<boolean | null>(data.hasBreastAbnormalities),
    nippleAbnormalitiesNotes: new FormControl<string | null>(data.nippleAbnormalitiesNotes),
    breastAbnormalitiesNotes: new FormControl<string | null>(data.breastAbnormalitiesNotes),
    supportPersonAtBedside: new FormControl<boolean | null>(data.supportPersonAtBedside),
    supportPersonType: new FormControl<string | null>(data.supportPersonType),
    supportPersonName: new FormControl<string | null>(data.supportPersonName),
    otherFindings: new FormControl<string | null>(data.otherFindings),
  });
}

function generalAppearanceAssessmentToPDF(pdf: AssessmentPdfGenerator, assessment: GeneralAppearanceAssessment, patient: Patient) {
  pdf.addSectionTitle("General Appearance");
  pdf.pdf.setFont("Helvetica", "normal");
  pdf.addTwoColumnText("LOC", assessment.loc,
    "Orientation", assessment.orientation.join(", "));
  pdf.addTwoColumnText("Lethargic?", assessment.lethargic ? "Yes" : "No",
    "Inappropriate for Age?", assessment.inappropriateForAge ? "Yes" : "No");
  pdf.addTwoColumnText("In Distress?", assessment.distress ? "Yes" : "No", "", "");
  if (patient.isObstetric()) {
    pdf.addOneColumnText("Not Bonding with Baby Appropriately", assessment.babyBonding ? "Yes" : "No");
    pdf.addTwoColumnText("Nipple Abnormalities", assessment.nippleAbnormalitiesNotes,
      "Breast Abnormalities", assessment.breastAbnormalitiesNotes)
  }
  if (patient.isPediatric()) {
    pdf.addOneColumnText("Support Person at Bedside", assessment.supportPersonAtBedside ? "Yes" : "No");
    if (assessment.supportPersonAtBedside) {
      pdf.addTwoColumnText("Support Person Type", assessment.supportPersonType,
        "Support Person Name", assessment.supportPersonName);
    }
  }
  pdf.addOneColumnText("Other Findings", assessment.otherFindings);
}

function formToGeneralAppearanceAssessment(fg: GeneralAppearanceFormGroup) {
  return new GeneralAppearanceAssessment({
    loc: fg.controls.loc.value!,
    orientation: fg.controls.orientation.value!,
    lethargic: fg.controls.lethargic.value!,
    inappropriateForAge: fg.controls.inappropriateForAge.value!,
    distress: fg.controls.distress.value!,
    babyBonding: fg.controls.babyBonding.value!,
    hasBreastAbnormalities: fg.controls.hasBreastAbnormalities.value!,
    nippleAbnormalitiesNotes: fg.controls.nippleAbnormalitiesNotes.value!,
    breastAbnormalitiesNotes: fg.controls.breastAbnormalitiesNotes.value!,
    supportPersonAtBedside: fg.controls.supportPersonAtBedside.value!,
    supportPersonType: fg.controls.supportPersonType.value!,
    supportPersonName: fg.controls.supportPersonName.value!,
    otherFindings: fg.controls.otherFindings.value!,
  });
}